import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSend, FiPaperclip, FiLock, FiUnlock, FiFlag, FiAtSign, FiCheck, FiUser, FiFileText } = FiIcons;

const mockMessages = [
  {
    id: 1,
    sender: { name: 'Dr. Sarah Smith', role: 'Primary Surgeon', avatar: '👩‍⚕️' },
    content: 'Patient is ready for the implant procedure. All pre-op clearances are complete.',
    timestamp: '2024-01-15 09:30',
    priority: 'high',
    encrypted: true,
    attachments: [],
    readBy: ['Dr. Mike Davis', 'Dr. Lisa Wilson'],
    mentions: []
  },
  {
    id: 2,
    sender: { name: 'Dr. Mike Davis', role: 'Oral Surgeon', avatar: '👨‍⚕️' },
    content: 'I reviewed the CBCT scan. Bone density looks good for immediate placement. @Dr.Smith can we schedule for next Tuesday?',
    timestamp: '2024-01-15 10:15',
    priority: 'normal',
    encrypted: false,
    attachments: ['cbct_scan.pdf'],
    readBy: ['Dr. Sarah Smith'],
    mentions: ['Dr.Smith']
  },
  {
    id: 3,
    sender: { name: 'Dr. Lisa Wilson', role: 'Prosthodontist', avatar: '👩‍⚕️' },
    content: 'Custom abutment design is ready. I will share the 3D render for your review.',
    timestamp: '2024-01-15 11:00',
    priority: 'normal',
    encrypted: true,
    attachments: ['abutment_design.stl', 'treatment_plan.pdf'],
    readBy: [],
    mentions: []
  },
  {
    id: 4,
    sender: { name: 'Patient - John Doe', role: 'Patient', avatar: '👤' },
    content: 'I have completed all the required lab work. Results should be available in the patient portal.',
    timestamp: '2024-01-15 14:30',
    priority: 'normal',
    encrypted: true,
    attachments: [],
    readBy: ['Dr. Sarah Smith', 'Dr. Mike Davis'],
    mentions: []
  }
];

const participants = [
  { id: 'dr-smith', name: 'Dr. Sarah Smith', role: 'Primary Surgeon' },
  { id: 'dr-davis', name: 'Dr. Mike Davis', role: 'Oral Surgeon' },
  { id: 'dr-wilson', name: 'Dr. Lisa Wilson', role: 'Prosthodontist' },
  { id: 'patient', name: 'John Doe', role: 'Patient' }
];

const SecureMessaging = ({ dealId }) => {
  const [messages, setMessages] = useState(mockMessages);
  const [newMessage, setNewMessage] = useState('');
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [priority, setPriority] = useState('normal');
  const [showMentions, setShowMentions] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [typingUsers, setTypingUsers] = useState([]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;

    const currentUser = { name: 'Dr. Sarah Smith', role: 'Primary Surgeon', avatar: '👩‍⚕️' };
    
    const message = {
      id: messages.length + 1,
      sender: currentUser,
      content: newMessage,
      timestamp: new Date().toLocaleString(),
      priority: priority,
      encrypted: isEncrypted,
      attachments: [],
      readBy: [],
      mentions: extractMentions(newMessage)
    };

    setMessages([...messages, message]);
    setNewMessage('');
    setPriority('normal');
    
    setTypingUsers(['Dr. Mike Davis']);
    setTimeout(() => setTypingUsers([]), 2000);
  };

  const extractMentions = (text) => {
    const mentionRegex = /@(\w+)/g;
    const mentions = [];
    let match;
    while ((match = mentionRegex.exec(text)) !== null) {
      mentions.push(match[1]);
    }
    return mentions;
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      console.log('Files to upload:', files);
    }
  };

  const insertMention = (participant) => {
    setNewMessage(newMessage + `@${participant.name.replace(' ', '')} `);
    setShowMentions(false);
  };

  return (
    <div className="flex flex-col h-[600px] bg-white rounded-lg border border-gray-200">
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Secure Messages</h3>
            <p className="text-sm text-gray-600">HIPAA-compliant communication</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsEncrypted(!isEncrypted)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isEncrypted ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <SafeIcon icon={isEncrypted ? FiLock : FiUnlock} />
              {isEncrypted ? 'Encrypted' : 'Normal'}
            </button>
            <div className="relative">
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="appearance-none pl-8 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              >
                <option value="normal">Normal</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
              <SafeIcon icon={FiFlag} className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 bg-gray-50">
        <AnimatePresence>
          {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex gap-3 ${message.sender.name === 'Dr. Sarah Smith' ? 'flex-row-reverse' : ''}`}
            >
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-lg ${
                message.sender.role === 'Patient' ? 'bg-blue-100' : 'bg-green-100'
              }`}>
                {message.sender.avatar}
              </div>
              <div className={`flex-1 max-w-lg ${message.sender.name === 'Dr. Sarah Smith' ? 'items-end' : ''}`}>
                <div className={`flex items-center gap-2 mb-1 ${message.sender.name === 'Dr. Sarah Smith' ? 'justify-end' : ''}`}>
                  <span className="font-medium text-sm text-gray-900">{message.sender.name}</span>
                  <span className="text-xs text-gray-500">{message.sender.role}</span>
                  <span className="text-xs text-gray-400">• {message.timestamp}</span>
                  {message.encrypted && <SafeIcon icon={FiLock} className="h-3 w-3 text-green-600" />}
                </div>
                
                <div className={`relative group ${message.sender.name === 'Dr. Sarah Smith' ? 'text-right' : ''}`}>
                  <div className={`inline-block px-4 py-2 rounded-lg ${
                    message.sender.name === 'Dr. Sarah Smith' 
                      ? 'bg-brand-primary text-white' 
                      : 'bg-white text-gray-900 border border-gray-200'
                  }`}>
                    {message.content}
                  </div>
                </div>

                {message.attachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {message.attachments.map((file, index) => (
                      <div key={index} className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-lg text-sm">
                        <SafeIcon icon={FiFileText} className="h-4 w-4 text-gray-500" />
                        <span className="text-gray-700">{file}</span>
                      </div>
                    ))}
                  </div>
                )}

                {message.readBy.length > 0 && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                    <SafeIcon icon={FiCheck} className="h-3 w-3" />
                    <span>Read by {message.readBy.length}</span>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {typingUsers.length > 0 && (
          <div className="flex gap-3 items-center text-sm text-gray-500">
            <div className="flex gap-1">
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
            <span>{typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="border-t border-gray-200 px-6 py-4 bg-white">
        <div className="flex items-center gap-3">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiPaperclip} />
          </button>
          
          <div className="relative flex-1">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Type your message..."
              className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
            
            <div className="absolute right-2 top-2 flex gap-1">
              <button
                onClick={() => setShowMentions(!showMentions)}
                className="p-1 text-gray-500 hover:text-gray-700 rounded"
              >
                <SafeIcon icon={FiAtSign} />
              </button>
            </div>

            {showMentions && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                <div className="p-2">
                  {participants.map((participant) => (
                    <button
                      key={participant.id}
                      onClick={() => insertMention(participant)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded flex items-center gap-2"
                    >
                      <SafeIcon icon={FiUser} className="h-4 w-4 text-gray-500" />
                      <div>
                        <div className="font-medium text-sm">{participant.name}</div>
                        <div className="text-xs text-gray-500">{participant.role}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            className="p-3 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <SafeIcon icon={FiSend} />
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileUpload}
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.dcm,.stl"
        />
      </div>
    </div>
  );
};

export default SecureMessaging;